/**
 * What a deal's working sheet shows and writes (PLAN-ENCRE-OUTILS O3): its status in words, the
 * pipeline's stages, the gestures that close or reopen it, the money it brings, and the changes an
 * edit makes — only what changed, so a save never writes back what someone else changed meanwhile.
 * Pure, but for the amounts it words: they pass through the presentation mode, fixed when the page loads.
 */
import type { CRMOpportunity, CRMPipelineStage } from '../../types/crm';
import { sensitive } from '../presentationMode';

/** The status as the sheet says it. */
export const OPPORTUNITY_STATUS_LABEL: Record<CRMOpportunity['status'], string> = {
  open: 'En cours',
  won: 'Gagnée',
  lost: 'Perdue',
};

export const REVENUE_TYPE_LABEL: Record<CRMOpportunity['revenue_type'], string> = {
  recurring: 'Récurrent',
  one_time: 'Ponctuel',
  mixed: 'Mixte',
};

/** Stages past the pipeline (order 9 and up) are the onboarding's (`moveCard`'s rule). */
const ONBOARDING_FROM = 9;

/** The pipeline's stages in order, and the deal's own stage when it sits past them (onboarding). */
export function stageChoices(stages: readonly CRMPipelineStage[], currentId: string | null): CRMPipelineStage[] {
  return stages
    .filter((s) => s.order_index < ONBOARDING_FROM || s.id === currentId)
    .sort((a, b) => a.order_index - b.order_index);
}

export type OpportunityPatch = Partial<
  Pick<
    CRMOpportunity,
    | 'name'
    | 'stage_id'
    | 'is_onboarding'
    | 'amount'
    | 'probability'
    | 'close_date'
    | 'target_close_date'
    | 'revenue_type'
    | 'one_time_amount'
    | 'stripe_product_id'
    | 'custom_product_name'
    | 'calculated_mrr'
    | 'status'
    | 'reason_lost'
  >
>;

/** Moving to a stage: the onboarding flag follows it, as the kanban moves it. */
export function stagePatch(stage: CRMPipelineStage): OpportunityPatch {
  return { stage_id: stage.id, is_onboarding: stage.order_index >= ONBOARDING_FROM };
}

const plain = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();

/** The pipeline's « Gagné » or « Perdu » stage, by its name. */
export function outcomeStage(stages: readonly CRMPipelineStage[], outcome: 'won' | 'lost'): CRMPipelineStage | null {
  const word = outcome === 'won' ? 'gagne' : 'perdu';
  return stages.find((s) => s.order_index < ONBOARDING_FROM && plain(s.name).startsWith(word)) ?? null;
}

/** A day as a `date` column and a date field read it, in the user's day. */
export function today(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Closing a deal, the rule Otto applies (`apply_opportunity_change`): its status, NOW as the day it
 * closed — `close_date` is the real closing day, never the planned one some deals still carry
 * (relecture du 13/10/2026: a won deal kept « 31 déc. » and counted in the wrong month) —, the
 * pipeline's column of that outcome, and for a lost one why. A deal in an onboarding column keeps it.
 */
export function closePatch(
  deal: Pick<CRMOpportunity, 'stage_id'>,
  stages: readonly CRMPipelineStage[],
  outcome: 'won' | 'lost',
  reason: string | null = null,
  now: Date = new Date(),
): OpportunityPatch {
  const current = stages.find((s) => s.id === deal.stage_id);
  const stage = current && current.order_index >= ONBOARDING_FROM ? null : outcomeStage(stages, outcome);
  return {
    status: outcome,
    close_date: now.toISOString(),
    ...(stage ? stagePatch(stage) : {}),
    ...(outcome === 'lost' ? { reason_lost: reason?.trim() || null } : {}),
  };
}

/** Reopening: open again, neither a closing day nor a reason for a loss left behind. */
export const REOPEN_PATCH: OpportunityPatch = { status: 'open', close_date: null, reason_lost: null };

/** « 1 200 € » — `null` when there is no amount. */
export function money(amount: number | null | undefined, unit = ''): string | null {
  if (amount == null) return null;
  return sensitive(`${amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €${unit}`);
}

/** « 720 €/mois · 8 640 €/an », from what the database derives. */
export function recurringLabel(deal: Pick<CRMOpportunity, 'calculated_mrr' | 'calculated_arr'>): string | null {
  return [money(deal.calculated_mrr, '/mois'), money(deal.calculated_arr, '/an')].filter(Boolean).join(' · ') || null;
}

/** The product's name: the catalogue's, else the one typed by hand. Never its key. */
export function productLabel(deal: Pick<CRMOpportunity, 'custom_product_name'> & { product: { name: string } | null }): string | null {
  return deal.product?.name?.trim() || deal.custom_product_name?.trim() || null;
}

/** A `date` (« 2026-12-31 ») as is, a timestamp as its day in the user's time — what a date field shows. */
export function dayOf(value: string | null | undefined): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/u.test(value)) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : today(d);
}

/** « 12 oct. 2026 » from a `date` or a timestamp. */
export function dateLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** A contact of the deal: those named on it first, with their role, then the account's. */
export interface DealContact {
  id: string;
  first_name: string;
  last_name: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
}

export function dealContacts(
  named: readonly { role: string | null; contact: DealContact | null }[],
  ofAccount: readonly DealContact[],
): DealContact[] {
  const seen = new Set<string>();
  const out: DealContact[] = [];
  for (const { role, contact } of named) {
    if (!contact || seen.has(contact.id)) continue;
    seen.add(contact.id);
    out.push({ ...contact, title: [role?.trim(), contact.title].filter(Boolean).join(' · ') || null });
  }
  for (const contact of ofAccount) {
    if (seen.has(contact.id)) continue;
    seen.add(contact.id);
    out.push(contact);
  }
  return out;
}
